import { formatDistanceStrict } from "date-fns";
import type { LandscapeRunStatus, WorkRunKind } from "@/types/landscape-log";
import type { ConsolidatedLandscape } from "@/types/landscape";

/**
 * The shared work-run vocabulary (`components/work-run-log.md § The
 * screen`, `§ The three hand-written mirrors`). Both the landscape log
 * screen (`WEB-002`) and the landscape rows (`WEB-003`) import from here
 * rather than re-declaring a label, a threshold or the open/closed rule
 * locally -- that split is how the product ended up with three words for
 * one run in the first place.
 */

/**
 * The six statuses in lifecycle order -- the same order the KPI cards, the
 * status filter options and the backend's `sort=status` rank all use
 * (`WorkRunStatus::lifecycleOrder()`). Never re-derive this order locally.
 */
export const RUN_STATUS_ORDER: readonly LandscapeRunStatus[] = [
  "queued",
  "running",
  "stopped",
  "succeeded_with_keywords",
  "succeeded_without_keywords",
  "failed",
];

export const RUN_STATUS_LABEL: Record<LandscapeRunStatus, string> = {
  queued: "Queued",
  running: "In Progress",
  stopped: "Stopped",
  succeeded_with_keywords: "Completed",
  succeeded_without_keywords: "No keywords",
  failed: "Failed",
};

/**
 * The label for a known status, `"Unknown"` for `null` (no run row), and
 * the raw value itself for an unrecognised non-null string.
 *
 * That third branch is not defensive decoration for *this* release: the
 * browser it protects is the one still holding the *old* bundle after the
 * *next* vocabulary change, which this code cannot reach. Rendering the raw
 * value is the difference between that future deploy producing a stale
 * label and producing a white screen (`components/work-run-log.md § The
 * screen`, "Status rendering must be total").
 */
export function runStatusLabel(status: LandscapeRunStatus | string | null): string {
  if (status === null) return "Unknown";
  if (status in RUN_STATUS_LABEL) return RUN_STATUS_LABEL[status as LandscapeRunStatus];

  return status;
}

/**
 * `true` only for the two endings that produced a landscape to open. The
 * single shared predicate the three status gates
 * (`ConsolidatedLandscapeRow`, `LandscapeRow`, `HistoryRow`) read instead of
 * each re-deriving their own array of "finished" strings.
 */
export function isLandscapeRunOpenable(status: LandscapeRunStatus | string | null): boolean {
  return status === "succeeded_with_keywords" || status === "succeeded_without_keywords";
}

/** The label for each `WorkRunKind`, read next to `RUN_STATUS_LABEL`. */
export const RUN_KIND_LABEL: Record<WorkRunKind, string> = {
  landscape: "Landscape",
  keyword_bank: "Keyword bank",
  seo_strategy: "SEO strategy",
};

/**
 * The label for a known kind, `"Unknown"` for `null`, and the raw value
 * itself for an unrecognised non-null kind -- the same three branches as
 * `runStatusLabel`, for the same reason: a `Record<…>` lookup with a
 * missing key renders `undefined` and `strictNullChecks: false` will not
 * say so.
 */
export function runKindLabel(kind: WorkRunKind | string | null): string {
  if (kind === null) return "Unknown";
  if (kind in RUN_KIND_LABEL) return RUN_KIND_LABEL[kind as WorkRunKind];

  return kind;
}

/**
 * `true` for exactly the three statuses a run can still be "in flight" as
 * -- `queued`, `running`, `stopped` -- the shared open-run predicate the
 * landscape log poll and the keyword bank run poll (`WEB-003`) both read.
 *
 * Deliberately not named with "open": `isLandscapeRunOpenable` above is
 * already true for exactly the opposite set -- the two endings that
 * produced a landscape to open. Two predicates in one file sharing that
 * word with opposite meanings is the collision this file exists to
 * prevent.
 */
export function isRunInFlight(status: LandscapeRunStatus | string | null): boolean {
  return status === "queued" || status === "running" || status === "stopped";
}

/**
 * Poll interval for work-run screens (FR-11/NFR-2) -- one number for both
 * the landscape log and the keyword bank run poll (`WEB-003`).
 */
export const RUN_POLL_INTERVAL_MS = 15_000;

/**
 * The keyword-count cell, on both screens, in one rule
 * (`components/work-run-log.md § The screen`): the number once the run
 * reached an ending -- the two openable endings, or `failed`, which ended
 * without keywords -- and an em dash while nothing has been counted yet.
 * `0` on `succeeded_without_keywords` is a real measurement and renders as
 * `0`, never as a placeholder.
 */
export function landscapeRunKeywordCountDisplay(
  status: LandscapeRunStatus | string | null,
  keywordCount: number,
): string | number {
  const reachedEnding = isLandscapeRunOpenable(status) || status === "failed";

  return reachedEnding ? keywordCount : "—";
}

/**
 * Elapsed time (FR-12), one rule for both screens
 * (`components/work-run-log.md § The screen`, the elapsed table):
 *
 * - `null` status or `queued` -- `"—"`, it has not started
 * - `running` -- `now - startedAt`, ticking; `"—"` if `startedAt` is null
 * - `stopped` -- `now - stateChangedAt`, ticking -- *how long it has been
 *   that way*. Never `now - startedAt`: a progress-only report moves
 *   `state_changed_at` without moving `started_at`, so that would report
 *   the age of the attempt rather than the age of the silence.
 * - the three endings -- `endedAt - (startedAt ?? requestedAt)`, frozen;
 *   `"—"` if `endedAt` is null, or if neither a started nor a requested
 *   instant is available. Several reachable backend paths (AUT-351) end a
 *   run without ever reporting `running` -- a batch death, a job dying
 *   before `processing`, a resolution failure reported ahead of the run's
 *   own `running` report -- so `started_at` stays null on rows that are
 *   otherwise fully terminal, with both `requested_at` and `ended_at`
 *   populated. `started_at` records the *observed* start; `requested_at`
 *   records the *ask*. A terminal row with no observed start still has a
 *   real elapsed span -- the screen already shows both ends of it (Date
 *   Started renders `requested_at`, Completed renders `ended_at`) -- so
 *   falling back to `requested_at` reports that span instead of the
 *   placeholder the screen showed despite both dates being filled in.
 */
export function landscapeRunElapsedLabel(
  status: LandscapeRunStatus | string | null,
  startedAt: string | null,
  endedAt: string | null,
  stateChangedAt: string | null,
  requestedAt: string | null,
  now: Date,
): string {
  if (status === null || status === "queued") return "—";

  if (status === "running") {
    if (!startedAt) return "—";
    return formatDistanceStrict(new Date(startedAt), now);
  }

  if (status === "stopped") {
    if (!stateChangedAt) return "—";
    return formatDistanceStrict(new Date(stateChangedAt), now);
  }

  // The three endings: succeeded_with_keywords, succeeded_without_keywords, failed.
  if (!endedAt) return "—";
  const start = startedAt ?? requestedAt;
  if (!start) return "—";
  return formatDistanceStrict(new Date(start), new Date(endedAt));
}

/**
 * Which row a phrase group gets on the Landscapes tab
 * (`components/landscape.md § Which row a phrase group gets (FR-8)`):
 * any openable version makes it a `landscape`; otherwise the newest
 * version (`instances[0]`) decides -- `queued`/`running` is `creating`,
 * `failed` is `failed`; everything else (stopped, null, unknown, no
 * versions) stays a `landscape`.
 */
export function landscapeGroupRowKind(group: ConsolidatedLandscape): "creating" | "failed" | "landscape" {
  const instances = group.instances ?? [];

  if (instances.some((instance) => isLandscapeRunOpenable(instance.status))) return "landscape";

  const newest = instances[0]?.status ?? null;
  if (newest === "queued" || newest === "running") return "creating";
  if (newest === "failed") return "failed";

  return "landscape";
}

/**
 * Mirrors the backend's user-facing failure texts
 * (`ScrapeAndStartLandscape` constants, `FailOrphanedLandscape::QUEUE_FAILED_STATUS_INFO`,
 * the AUT-462 guardrail `sprintf`, and two legacy texts still on old rows).
 */
export const FAILED_BUILD_TEXT =
  "Something went wrong while building this landscape. Please try again, and contact support if it keeps happening.";

const FAILED_REASON_TEXTS: ReadonlySet<string> = new Set([
  FAILED_BUILD_TEXT,
  "Something went wrong while loading keywords for this landscape. Please try again, and contact support if it keeps happening.",
  "Landscapes can't be built right now. Please contact support.",
  "The brand of this landscape was not found. It may have been deleted.",
  "The product of this landscape was not found. It may have been removed from Signal.",
  "Something went wrong while adding competitors to this landscape. Please try again, and contact support if it keeps happening.",
  "Landscape run could not be queued — please try again.",
  "Could not find enough competitors on Amazon for this keyword",
  "This build never got past its first step and was closed. Run the landscape again to build it.",
]);

const FAILED_REASON_PATTERNS: readonly RegExp[] = [
  /^Found only \d+ competitors for this keyword \(need at least 3\)\. Try a broader keyword\.$/,
  /^Could not find \d+ competitors on Amazon for this keyword$/,
];

/**
 * What a failed landscape may tell the user: `null` when there is no
 * `status_info`, the text itself when it is a known user-facing one, and
 * `FAILED_BUILD_TEXT` for anything else (old rows still hold internal
 * errors and progress texts). Exact, case-sensitive match -- no trimming:
 * the backend writes these strings verbatim, so any drift is "unknown".
 */
export function failedLandscapeReason(statusInfo: string | null | undefined): string | null {
  if (!statusInfo) return null;
  if (FAILED_REASON_TEXTS.has(statusInfo)) return statusInfo;
  if (FAILED_REASON_PATTERNS.some((pattern) => pattern.test(statusInfo))) return statusInfo;

  return FAILED_BUILD_TEXT;
}
